import { useTranslation } from "@localization/useTranslation";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useSelectableInputFields } from "@common/hooks/useSelectableItemQueries.ts";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types.ts";
import { useFeatureFlags } from "../../../auth/planProvider/PlanProvider.tsx";
import { DatePeriodConfiguration, EntityTemplate, EntityTemplateField, FormulaToken, TemplateEntityType } from "../../EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "../../inputFields/InputFields.types.ts";
import { calculateFormula, describeFormula, getFieldIds, parseFormula, sumTokens } from "./calculatedField.utils.ts";
import { ComputedFieldEntityValues, parseDatePeriod } from "./datePeriod.utils.ts";
import { ComputedFieldTemplateEditing } from "./computedField.types.ts";

export interface CustomCalculatedFieldProps {
    field: EntityTemplateField;
    fieldDescription: string | undefined;
    isLoading: boolean;
    isPreview: boolean;
    errorText?: string;
    templateEntityType: TemplateEntityType;
    computedFieldEditing?: ComputedFieldTemplateEditing;
    entityTemplateId: string | undefined;
    entity: ComputedFieldEntityValues;
}

export function useCustomCalculatedField(props: CustomCalculatedFieldProps) {
    const { translateNumber } = useTranslation();
    const featureFlags = useFeatureFlags();

    const tokens = parseFormula(props.field.configurationData);
    const hasPeriodOperand = tokens.some((token) => token.fieldType === InputFieldTypeEnum.DatePeriodField);

    // The template editor names the fields through its own chips, so names are only looked up for the value shown on the entity itself
    const { data: inputFields } = useSelectableInputFields(props.templateEntityType, !props.isPreview);

    // A period is set up on its own template field, possibly on another step, so its dates are read from the whole template.
    // Every entity page has already loaded the template under this url, so this is read from the cache.
    const { data: template } = useDotLegalQuery<EntityTemplate>(`/entity-templates/${props.entityTemplateId}`, {
        enabled: !props.isPreview && hasPeriodOperand && !!props.entityTemplateId,
    });

    const result = calculateFormula(tokens, props.entity, getPeriodConfigurations(template));
    const amountFields = (props.computedFieldEditing?.formulaFields ?? []).filter((field) => field.type === InputFieldTypeEnum.AmountField);

    return {
        tokens,
        isFormulaEditorEnabled: featureFlags.isEnabled(FeatureFlag.CalculatedFieldFormulas),
        amountFieldOptions: amountFields.map((field) => ({ id: field.id, name: field.name })),
        includedAmountFieldIds: getFieldIds(tokens),
        onIncludedAmountFieldsChange: (fieldIds: Array<string>) => onFormulaChange(sumTokens(amountFields.filter((field) => fieldIds.includes(field.id)))),
        onFormulaChange,
        valueText: result.value === undefined ? "" : translateNumber(result.value, { maximumFractionDigits: 2 }),
        currencyCode: result.currencyCode,
        hasConflictingCurrencies: result.hasConflictingCurrencies,
        emptyPeriodNames: result.emptyPeriodFieldIds.map(getInputFieldName).join(", "),
        formulaDescription: tokens.length > 0 ? describeFormula(tokens, (token) => getInputFieldName(token.fieldId!)) : undefined,
    };

    function getInputFieldName(fieldId: string) {
        return inputFields?.find((inputField) => inputField.id === fieldId)?.name ?? "";
    }

    function onFormulaChange(changedTokens: Array<FormulaToken>) {
        props.computedFieldEditing?.onFormulaChange(props.field.id, changedTokens);
    }
}

function getPeriodConfigurations(template: EntityTemplate | undefined): Map<string, DatePeriodConfiguration> {
    const periodFields = (template?.steps ?? [])
        .flatMap((step) => step.fields)
        .filter((field) => field.customInputFieldType === InputFieldTypeEnum.DatePeriodField);

    return new Map(
        periodFields
            .map((field) => [field.customInputFieldId!, parseDatePeriod(field.configurationData)] as const)
            .filter((entry): entry is readonly [string, DatePeriodConfiguration] => entry[1] !== undefined)
    );
}
